import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CRow,
  CForm,
  CFormInput,
  CFormLabel,
  CFormSelect,
  CFormCheck,
} from "@coreui/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  createRoutePoint,
  getSingleRoutePoint,
  updateRoutePoint,
} from "../../services/route-point.services";
import { useParams } from "react-router-dom";
import { getActiveCountryList } from "src/services/country.services";
import Select from "react-select";

const schema = z.object({
  name: z.string().min(1, "Please type name"),
  locationCode: z.string().min(1, "Please type location code"),
  city: z.string().min(1, "Please type city"),
  country: z.string().min(1, "Please select a country"),
});

const CreateRoutePointForm = () => {
  const [userInfo, setUserInfo] = useState(
    localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null
  );
  const [routePointId, setRoutePointId] = useState("");
  const [routePointData, setRoutePointData] = useState(null);
  const { id } = useParams();
  const [countryOptions, setCountryOptions] = useState([]);
  const [selectedCountryOption, setSelectedCountryOption] = useState(null);

  useEffect(() => {
    if (userInfo?.country) {
      setValue("country", userInfo.country._id);
      setSelectedCountryOption({
        value: userInfo.country._id,
        label: `${userInfo.country.name} - ${userInfo.country.code}`,
      });
    }
    if (userInfo?.userType === "superAdmin") getActiveCountry();
  }, []);

  const getActiveCountry = () => {
    const limit = 100;
    const page = 1;
    getActiveCountryList({ limit, page, status: true })
      .then(async (response) => {
        const countryData = response.data;
        let options = [];
        countryData.forEach((element) => {
          const option = {
            value: `${element._id}`,
            label: `${element.name} - ${element.code}`,
          };
          options.push(option);
        });
        setCountryOptions(options);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    if (id) {
      setRoutePointId(id);
      getRoutePointInfo(id);
    }
    console.log("id======", id);
  }, [id]);

  const getRoutePointInfo = (id) => {
    // setIsLoading(true)
    getSingleRoutePoint(id)
      .then(async (response) => {
        setRoutePointData(response.data);
        const { name, locationCode, city, country } = response.data;
        setValue("name", name);
        setValue("locationCode", locationCode);
        setValue("city", city);

        if (country) {
          setSelectedCountryOption({
            label: `${country.name} - ${country.code}`,
            value: country._id,
          });
          setValue("country", country._id);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      locationCode: "",
      city: "",
      country: selectedCountryOption ? selectedCountryOption.value : "",
    },
    resolver: zodResolver(schema),
  });

  const [defaultErrorMessage, setDefaultErrorMessage] = useState(
    '"Something went wrong. Please try again"'
  );

  const onSubmit = (data) => {
    console.log(routePointId);
    if (routePointId) {
      toast
        .promise(
          updateRoutePoint(data, routePointId),
          {
            loading: "Loading",
            success: "Route Point Updated Succesfully",
            error: (err) => {
              return err.response &&
                err.response.data &&
                err.response.data.message
                ? err.response.data.message
                : defaultErrorMessage;
            },
          },
          {
            id: "routePoint",
          }
        )
        .then(async (response) => {
          console.log("response", response);
        })
        .catch((error) => console.error(error));
    } else {
      toast
        .promise(
          createRoutePoint(data),
          {
            loading: "Loading",
            success: "Route Point Created Succesfully",
            error: (err) => {
              return err.response &&
                err.response.data &&
                err.response.data.message
                ? err.response.data.message
                : defaultErrorMessage;
            },
          },
          {
            id: "routePoint",
          }
        )
        .then(async (response) => {
          console.log("response", response);
          reset();
          setSelectedCountryOption(null);
        })
        .catch((error) => console.error(error));
    }
  };

  const onError = (err) => {
    console.error(err);
  };

  const handleChangeCountry = (selectedOption) => {
    if (selectedOption) {
      setSelectedCountryOption(selectedOption);
      setValue("country", selectedOption.value);
    } else {
      setSelectedCountryOption("");
      setValue("country", "");
    }
  };

  return (
    <CCard className="mb-4">
      <CCardBody>
        <CRow>
          <CCol sm={5}>
            <h4 id="traffic" className="card-title mb-0">
              {routePointId ? "Edit Route Point" : "Create Route Point"}
            </h4>
          </CCol>
          <CCol sm={7} className="d-none d-md-block"></CCol>
        </CRow>
        <hr />
        <br />
        <CForm onSubmit={handleSubmit(onSubmit, onError)}>
          <CRow>
            <CCol sm={6}>
              <CRow className="mb-3">
                <CFormLabel htmlFor="name" className="col-sm-3 col-form-label">
                  Name:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput type="text" id="name" {...register("name")} />
                  {errors.name?.message && (
                    <p className="error_text">{errors.name?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="locationCode"
                  className="col-sm-3 col-form-label"
                >
                  Location Code:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput
                    type="text"
                    id="locationCode"
                    {...register("locationCode")}
                  />
                  {errors.locationCode?.message && (
                    <p className="error_text">{errors.locationCode?.message}</p>
                  )}
                </CCol>
              </CRow>
            </CCol>
            <CCol sm={6}>
              <CRow className="mb-3">
                <CFormLabel htmlFor="city" className="col-sm-3 col-form-label">
                  City:
                </CFormLabel>
                <CCol sm={8}>
                  <CFormInput type="text" id="city" {...register("city")} />
                  {errors.city?.message && (
                    <p className="error_text">{errors.city?.message}</p>
                  )}
                </CCol>
              </CRow>

              <CRow className="mb-3">
                <CFormLabel
                  htmlFor="country"
                  className="col-sm-3 col-form-label"
                >
                  Country:
                </CFormLabel>
                <CCol sm={8}>
                  <Select
                    value={selectedCountryOption}
                    onChange={handleChangeCountry}
                    options={countryOptions}
                    isClearable={true}
                    isDisabled={userInfo.userType !== "superAdmin"}
                  />
                  {errors.country?.message && (
                    <p className="error_text">{errors.country?.message}</p>
                  )}
                </CCol>
              </CRow>
            </CCol>
            <CRow className="mt-5">
              <div className="d-flex justify-content-end">
                <CButton type="submit" color="primary" className="px-5 py-2">
                  Submit
                </CButton>
              </div>
            </CRow>
          </CRow>
        </CForm>
      </CCardBody>
    </CCard>
  );
};

export default CreateRoutePointForm;
